import { useEffect, useRef } from 'react';
import type { RefObject } from 'react';

export interface UseDismissOptions {
  /** 활성화 여부 (기본: true). false 면 리스너를 부착하지 않는다 — 조건부 렌더/상태 게이팅용. */
  enabled?: boolean;
  /** 외부 클릭(mousedown)으로 닫힘 (기본: true). portal 드롭다운은 ignoreRefs 로 "내부"로 등록. */
  outsideClick?: boolean;
  /** ESC 키로 닫힘 (기본: false). 기존 동작 보존 — ESC 닫힘이 있던 곳만 명시적으로 켬. */
  escape?: boolean;
  /** "내부"로 취급할 추가 ref 배열. body portal 자식 클릭이 외부 클릭으로 오인되지 않도록. */
  ignoreRefs?: ReadonlyArray<RefObject<Element | null>>;
  /** 리스너 부착 지연(ms, 기본: 0). 열림 클릭이 닫힘을 발동시키는 레이스 회피. */
  attachDelay?: number;
}

/**
 * 외부 클릭 / ESC 키로 요소를 닫는 범용 dismiss 훅.
 * 코드베이스 전역에서 수작업 반복되던 addEventListener('mousedown') + cleanup + (선택) ESC 패턴을 통합.
 * - 콜백/ignoreRefs 는 ref 로 추적 → 호출측 useCallback 불필요, 이펙트는 boolean 플래그 변경시만 재구독.
 * - 이벤트는 기존 동작 보존을 위해 mousedown 사용 (터치 강화는 별도 PR pointerdown 마이그레이션).
 */
export function useDismiss(
  ref: RefObject<Element | null>,
  onDismiss: () => void,
  options: UseDismissOptions = {}
): void {
  const {
    enabled = true,
    outsideClick = true,
    escape = false,
    ignoreRefs,
    attachDelay = 0,
  } = options;

  const onDismissRef = useRef(onDismiss);
  const ignoreRefsRef = useRef(ignoreRefs);
  useEffect(() => {
    onDismissRef.current = onDismiss;
    ignoreRefsRef.current = ignoreRefs;
  }, [onDismiss, ignoreRefs]);

  useEffect(() => {
    if (!enabled) return;

    const handleOutside = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (ref.current?.contains(target)) return;
      if (ignoreRefsRef.current?.some((r) => r.current?.contains(target))) return;
      onDismissRef.current();
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onDismissRef.current();
    };

    const attach = () => {
      if (outsideClick) document.addEventListener('mousedown', handleOutside);
      if (escape) document.addEventListener('keydown', handleEscape);
    };
    const detach = () => {
      if (outsideClick) document.removeEventListener('mousedown', handleOutside);
      if (escape) document.removeEventListener('keydown', handleEscape);
    };

    if (attachDelay > 0) {
      const timeoutId = setTimeout(attach, attachDelay);
      return () => {
        clearTimeout(timeoutId);
        detach();
      };
    }

    attach();
    return detach;
  }, [enabled, outsideClick, escape, attachDelay, ref]);
}
